import React, { useRef, useState } from "react";
import cx from "classnames";
import { useDispatch, useSelector } from "react-redux";

import { retrieveCart } from "api/requests/retrieveCart";
import { Location } from "custom-types/Location";
import useDomainCountryCode from "hooks/useDomainCountryCode";
import useUserFulfillmentPreference from "hooks/useUserFulfillmentPreference";
import { updateLocation } from "redux/action-creators/location";
import {
  GET_CART_FAILURE,
  GET_CART_SUCCESS,
  SET_FOR_DELIVERY_OPTION,
} from "redux/reducers/cart";
import { SET_DELIVERY_SERVICE_AREA } from "redux/reducers/cartDispensary";
import { SET_USER_DELIVERY_ADDRESS } from "redux/reducers/location";
import { getCartSessionId } from "redux/selectors/cart";
import {
  getCurrentStatuses,
  getCurrentStatusIsOpenForDelivery,
  getDeliveryServiceArea,
  getDispensaryIsOpenForPickupOrPreorder,
  getDispensarySchedules,
  getDispensaryWithDeliveryServiceAreaRanges,
} from "redux/selectors/cartDispensary";
import { getDeliveryAddress, getLocationState } from "redux/selectors/location";
import {
  compareCartContextAddresses,
  normalizeCartContextAddress,
} from "utils/cartContextUtils";
import { convertGooglePlaceIdToLocation } from "utils/convertGoogleGeocoderToLocation";

import DeliveryValidationModal from "components/botanic/DeliveryValidationModal";
import DeliveryValidationButton from "components/botanic/DeliveryValidationModal/DeliveryValidationButton";

import DeliveryTimeEstimate from "./DeliveryTimeEstimate";
import FulfillmentCardDelivery from "./FulfillmentCardDelivery";

type Props = {
  clickHandler: () => void;
  isEmbedded?: boolean;
  className?: string;
};

const DeliveryInfo = ({ clickHandler, isEmbedded, className }: Props) => {
  const dispatch = useDispatch();
  const dispensary = useSelector(getDispensaryWithDeliveryServiceAreaRanges);
  const isOpenForPickupOrPreorder = useSelector(
    getDispensaryIsOpenForPickupOrPreorder
  );
  const isOpenForDelivery = useSelector(getCurrentStatusIsOpenForDelivery);
  const deliveryServiceArea = useSelector(getDeliveryServiceArea);
  const deliveryAddress = useSelector(getDeliveryAddress);
  const userLocation = useSelector(getLocationState);
  const cartSessionId = useSelector(getCartSessionId);
  const currentStatuses = useSelector(getCurrentStatuses);
  const schedules = useSelector(getDispensarySchedules);
  const domainCountryCode = useDomainCountryCode();

  const { setUserFulfillmentPreference } = useUserFulfillmentPreference();

  const [showDeliveryValidation, setShowDeliveryValidation] = useState(false);

  const validationButtonRef = useRef<HTMLButtonElement>(null);

  const switchToPickup = () => {
    setUserFulfillmentPreference("pickup");
    dispatch({
      forDelivery: false,
      type: SET_FOR_DELIVERY_OPTION,
    });
    setShowDeliveryValidation(false);
  };

  const validateDelivery = async (address: Location) => {
    if (isEmbedded) {
      dispatch({
        address: address,
        type: SET_USER_DELIVERY_ADDRESS,
      });
    } else {
      getGeocoderLocation(address);
    }

    // Only get cart if new address has been entered.
    if (
      deliveryAddress &&
      !compareCartContextAddresses(deliveryAddress, address)
    ) {
      const cartContext = {
        deliveryAddress: normalizeCartContextAddress(address),
        fulfillmentMechanism: "delivery",
      };

      // Update the cart if on the pickup page to recalculate taxes, based on
      // new delivery address.
      const cartResponse = await retrieveCart({
        cartContext: cartContext,
        cartId: cartSessionId,
        domainCountryCode: domainCountryCode,
      });

      if (cartResponse.error) {
        dispatch({
          type: GET_CART_FAILURE,
        });
      } else if (cartResponse.cart) {
        dispatch({
          cart: cartResponse.cart,
          changes: cartResponse.changes,
          type: GET_CART_SUCCESS,
        });
      }
    }

    setShowDeliveryValidation(false);
  };

  /** Fills in extra address data from google that location picker cares about */
  const getGeocoderLocation = async (newAddress: Location) => {
    const placeId = newAddress?.placeId || userLocation?.place_id;

    if (!placeId) {
      return;
    }

    const location = await convertGooglePlaceIdToLocation(placeId);
    const cookieLocation = { ...newAddress, ...location };

    updateLocation(dispatch, cookieLocation);
    dispatch({
      address: cookieLocation,
      type: SET_USER_DELIVERY_ADDRESS,
    });
  };

  return (
    <React.Fragment>
      {isOpenForDelivery && (
        <div className={cx(className, "my-md")}>
          <DeliveryValidationButton
            ref={validationButtonRef}
            handleClick={() => setShowDeliveryValidation(true)}
            address={deliveryAddress?.formattedAddress || ""}
            inZone={!!deliveryServiceArea}
          />
          <DeliveryValidationModal
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
            complianceCallback={(deliveryZone: any) =>
              dispatch({
                deliveryServiceArea: deliveryZone.deliveryServiceArea,
                type: SET_DELIVERY_SERVICE_AREA,
              })
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
            defaultAddress={deliveryAddress as any}
            dispensary={{ ...dispensary, currentStatuses, schedules }}
            handlePrimaryAction={validateDelivery}
            handleSecondaryAction={switchToPickup}
            onDismissed={() => setShowDeliveryValidation(false)}
            primaryActionLabel="confirm address"
            returnFocusRef={validationButtonRef}
            showModal={showDeliveryValidation}
            secondaryActionLabel={
              isOpenForPickupOrPreorder ? "try pickup instead" : undefined
            }
            width={500}
          />
          {!dispensary.scheduledDeliveryEnabled && <DeliveryTimeEstimate />}
        </div>
      )}
      {!dispensary.scheduledDeliveryEnabled && (
        <FulfillmentCardDelivery
          dispensary={{ ...dispensary, currentStatuses, schedules }}
          fulfillmentCardProps={{
            clickHandler,
            style: {
              marginTop: `${
                !isOpenForDelivery || !deliveryServiceArea ? "16px" : ""
              }`,
              maxWidth: "unset",
            },
          }}
        />
      )}
    </React.Fragment>
  );
};

export default DeliveryInfo;
